import { useEffect } from "react";

const SITE_NAME = "TrendMing";
const BASE_URL = "https://trendming.com";
const DEFAULT_TITLE = "TrendMing - Latest Global News, AI, Tech & Trends";
const DEFAULT_DESCRIPTION =
  "TrendMing delivers the latest global trends, AI news, technology updates, business insights, health stories, and breaking world news.";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`;

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, extra = {}) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

function setJsonLd(id, data) {
  let el = document.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * SEO component — updates document <head> metadata on mount/update.
 *
 * Props:
 *   title        – page title (appended with " | TrendMing" unless it already contains site name)
 *   description  – meta description
 *   canonical    – canonical URL (defaults to current page URL)
 *   ogImage      – Open Graph image URL
 *   ogType       – "website" | "article" (default "website")
 *   noindex      – set true to prevent indexing
 *   structuredData – array of JSON-LD objects to inject
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noindex = false,
  structuredData = [],
}) {
  const fullTitle =
    title
      ? title.includes(SITE_NAME)
        ? title
        : `${title} | ${SITE_NAME}`
      : DEFAULT_TITLE;

  const canonicalUrl = canonical || `${BASE_URL}${window.location.pathname}`;

  useEffect(() => {
    // Title
    document.title = fullTitle;

    // Primary meta
    setMeta("description", description);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    // Canonical
    setLink("canonical", canonicalUrl);

    // Open Graph
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:site_name", SITE_NAME, "property");

    // Twitter Card
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    setMeta("twitter:card", "summary_large_image");

    // Structured data
    structuredData.forEach((schema, i) => {
      setJsonLd(`page-schema-${i}`, schema);
    });
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, noindex, structuredData]);

  return null;
}

// ─── Preset builders ────────────────────────────────────────────────────────

export function buildArticleSchema(article) {
  const canonicalUrl = article.slug
    ? `${BASE_URL}/news/${article.slug}`
    : `${BASE_URL}/article/${article.id}`;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.description || article.summary || DEFAULT_DESCRIPTION,
    image: article.thumbnail ? [article.thumbnail] : [DEFAULT_OG_IMAGE],
    datePublished: article.published,
    dateModified: article.published,
    author: {
      "@type": "Organization",
      name: article.source || SITE_NAME,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    url: canonicalUrl,
  };
}

export function buildCategorySchema(categoryLabel) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryLabel} News - ${SITE_NAME}`,
    url: `${BASE_URL}${window.location.pathname}`,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: `${BASE_URL}/`,
    },
  };
}
